import PropTypes from "prop-types";
import { useContext, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Avatar, Box, Button, IconButton, Tooltip, Typography } from "@mui/material";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import DashboardOutlinedIcon from "@mui/icons-material/DashboardOutlined";
import EventAvailableOutlinedIcon from "@mui/icons-material/EventAvailableOutlined";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import LogoutOutlinedIcon from "@mui/icons-material/LogoutOutlined";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";

import CounsellorAuthContext from "../../context/CounsellorAuthContext";

const getNestedValue = (obj, paths, fallback = "") => {
  for (const path of paths) {
    const value = path.split(".").reduce((acc, key) => acc?.[key], obj);
    if (value !== undefined && value !== null && String(value).trim() !== "") {
      return value;
    }
  }
  return fallback;
};

const EXPANDED_WIDTH = 308;
const COLLAPSED_WIDTH = 72;

const menuItems = [
  {
    key: "dashboard",
    label: "Dashboard",
    path: "/counsellor-portal/dashboard",
    icon: DashboardOutlinedIcon,
  },
  {
    key: "calendar",
    label: "Calendar",
    path: "/counsellor-portal/calendar",
    icon: CalendarMonthOutlinedIcon,
  },
  {
    key: "bookings",
    label: "Bookings",
    path: "/counsellor-portal/bookings",
    icon: EventAvailableOutlinedIcon,
  },
  {
    key: "payments",
    label: "Payments",
    path: "/counsellor-portal/payments",
    icon: PaymentsOutlinedIcon,
  },
  {
    key: "profile",
    label: "Profile",
    path: "/counsellor-portal/profile",
    icon: PersonOutlineOutlinedIcon,
  },
];

const CounsellorSidebar = ({ onLogout }) => {
  const [collapsed, setCollapsed] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { counsellorUser } = useContext(CounsellorAuthContext);

  const counsellor = useMemo(() => counsellorUser?.counsellor || {}, [counsellorUser]);
  const userInfo = useMemo(() => counsellorUser?.userInfo || {}, [counsellorUser]);
  const counsellorName = getNestedValue(
    counsellor,
    ["name", "full_name", "username"],
    getNestedValue(userInfo, ["name", "full_name", "username"], "Counsellor")
  );
  const counsellorPhoto = getNestedValue(
    counsellor,
    ["photo", "image", "avatar", "profile_image", "profileImage"],
    getNestedValue(userInfo, ["photo", "image", "avatar", "profile_image", "profileImage"], "")
  );

  const isActive = (item) => {
    if (location.pathname === "/counsellor-portal" && item.key === "dashboard") {
      return true;
    }

    return location.pathname === item.path;
  };

  return (
    <Box
      sx={{
        width: collapsed ? COLLAPSED_WIDTH : EXPANDED_WIDTH,
        height: "100%",
        bgcolor: "#2D315F",
        color: "#fff",
        flexShrink: 0,
        display: "flex",
        flexDirection: "column",
        transition: "width 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: collapsed ? "center" : "space-between",
          px: collapsed ? 2 : 2.25,
          py: 3.5,
          borderBottom: "1px solid rgba(255,255,255,0.12)",
          minHeight: 88,
        }}
      >
        <Box
          component="img"
          src={
            collapsed
              ? "/assets/images/swrizeLogoMini.png"
              : "/assets/images/swrizeLogo.png"
          }
          alt="Swrize"
          sx={{
            height: 28,
            maxWidth: collapsed ? 36 : 130,
            objectFit: "contain",
            flexShrink: 0,
          }}
        />

        {!collapsed && (
          <IconButton
            aria-label="Collapse counsellor sidebar"
            onClick={() => setCollapsed(true)}
            sx={{
              width: 36,
              height: 36,
              color: "#fff",
              border: "1px solid rgba(255,255,255,0.55)",
              borderRadius: "7px",
            }}
          >
            <KeyboardArrowLeftIcon />
          </IconButton>
        )}
      </Box>

      {!collapsed && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            textAlign: "center",
            pt: 3,
            pb: 2,
            px: 2,
          }}
        >
          <Avatar
            src={counsellorPhoto || undefined}
            sx={{
              width: 112,
              height: 112,
              bgcolor: "#FFA447",
              color: "#fff",
              fontSize: 44,
              fontWeight: 700,
              mb: 1.5,
            }}
          >
            {!counsellorPhoto ? counsellorName?.[0]?.toUpperCase() : null}
          </Avatar>

          <Typography
            sx={{
              fontSize: 20,
              fontWeight: 600,
              color: "#fff",
              opacity: 0.85,
            }}
          >
            {counsellorName ? `@${counsellorName}` : "@Counsellor"}
          </Typography>
        </Box>
      )}

      {collapsed && (
        <IconButton
          aria-label="Expand counsellor sidebar"
          onClick={() => setCollapsed(false)}
          sx={{
            width: 36,
            height: 36,
            color: "#fff",
            border: "1px solid rgba(255,255,255,0.55)",
            borderRadius: "7px",
            mx: "auto",
            my: 2,
          }}
        >
          <KeyboardArrowRightIcon />
        </IconButton>
      )}

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 1.5,
          px: collapsed ? 2 : 1.75,
          pt: 2,
          flex: 1,
        }}
      >
        {menuItems.map((item) => {
          const Icon = item.icon;
          const selected = isActive(item);
          const button = (
            <Button
              key={item.key}
              fullWidth
              onClick={() => navigate(item.path)}
              startIcon={<Icon sx={{ fontSize: 22 }} />}
              sx={{
                justifyContent: collapsed ? "center" : "flex-start",
                height: 45,
                px: collapsed ? 0 : 1.5,
                borderRadius: "8px",
                textTransform: "none",
                fontSize: 16,
                fontWeight: 500,
                color: "#fff",
                bgcolor: selected ? "#FFA447" : "transparent",
                minWidth: collapsed ? 40 : "auto",
                "&:hover": {
                  bgcolor: selected ? "#FFA447" : "rgba(255,255,255,0.08)",
                },
                "& .MuiButton-startIcon": {
                  mr: collapsed ? 0 : 1.5,
                },
              }}
            >
              <Box
                component="span"
                sx={{
                  overflow: "hidden",
                  opacity: collapsed ? 0 : 1,
                  width: collapsed ? 0 : "auto",
                  whiteSpace: "nowrap",
                }}
              >
                {item.label}
              </Box>
            </Button>
          );

          return collapsed ? (
            <Tooltip key={item.key} title={item.label} placement="right" arrow>
              <Box sx={{ display: "flex", justifyContent: "center" }}>
                {button}
              </Box>
            </Tooltip>
          ) : (
            button
          );
        })}
      </Box>

      <Box
        sx={{
          px: collapsed ? 2 : 1.75,
          py: 2,
          borderTop: "1px solid rgba(255,255,255,0.12)",
        }}
      >
        <Tooltip title={collapsed ? "Logout" : ""} placement="right" arrow>
          <Button
            fullWidth
            onClick={onLogout}
            startIcon={<LogoutOutlinedIcon sx={{ fontSize: 22 }} />}
            sx={{
              justifyContent: collapsed ? "center" : "flex-start",
              height: 45,
              px: collapsed ? 0 : 1.5,
              borderRadius: "8px",
              textTransform: "none",
              fontSize: 16,
              fontWeight: 500,
              color: "#fff",
              bgcolor: "transparent",
              minWidth: collapsed ? 40 : "auto",
              "&:hover": { bgcolor: "rgba(255,255,255,0.08)" },
              "& .MuiButton-startIcon": {
                mr: collapsed ? 0 : 1.5,
              },
            }}
          >
            <Box
              component="span"
              sx={{
                overflow: "hidden",
                opacity: collapsed ? 0 : 1,
                width: collapsed ? 0 : "auto",
                whiteSpace: "nowrap",
              }}
            >
              Logout
            </Box>
          </Button>
        </Tooltip>
      </Box>
    </Box>
  );
};

CounsellorSidebar.propTypes = {
  onLogout: PropTypes.func.isRequired,
};

export default CounsellorSidebar;
